import * as AWS from 'aws-sdk';
import sigV4Client from './sigV4Client';
import config from '../config';
import { getRequestCredentials } from './auth';

/**
 * HaftahaveErrorResponse
 * @typedef {{
 *      error: boolean,
 *      message: string,
 *  }} HaftahaveErrorResponse
 */

/**
 * HaftahaveSuccessResponse
 * @typedef {{status:string} | Object<*>} HaftahaveSuccessResponse
 */

/**
 * HaftahaveResponse
 * @typedef {HaftahaveErrorResponse | HaftahaveSuccessResponse} HaftahaveResponse
 */

/**
 * @param {string} path
 * @param {string} method
 * @param {Object} headers
 * @param {Object} queryParams
 * @param {Object} body
 * @param {string|null} apiGateway
 * @returns {Promise<HaftahaveResponse>}
 */
export async function makeAuthorizedRequest({
    path,
    method = 'GET',
    headers = {},
    queryParams = {},
    body,
    apiGateway = null,
}) {
    if (apiGateway && !config.aws.apiGateway[apiGateway]) {
        throw new Error(`makeAuthorizedRequest(): Unknown API gateway "${apiGateway}"`);
    }

    const requestCredentials = await getRequestCredentials();
    const clientOptions = {
        ...requestCredentials,
        region: config.aws.region,
        endpoint: apiGateway ? config.aws.apiGateway[apiGateway] : config.aws.apiGateway.merchantBackend,
    };
    const signedRequest = sigV4Client
        .newClient(clientOptions)
        .signRequest({
            method,
            path,
            headers,
            queryParams,
            body,
        });

    const response = await fetch(signedRequest.url, {
        method,
        headers: signedRequest.headers,
        body: body ? JSON.stringify(body) : body,
    });

    if (response.status !== 200) {
        let message;
        try {
            const payload = await response.json();
            if (payload.error && payload.message) {
                ({ message } = payload);
            } else {
                message = await response.text();
            }
        } catch (error) {
            message = await response.text();
        }

        throw new Error(message);
    }
    return response.json();
}

/**
 * @param {*} path
 * @param {*} method
 * @param {*} headers
 * @param {*} _queryParams
 * @param {*} body
 * @param {string|null} apiGateway
 * @returns {Promise<HaftahaveResponse>}
 */
export async function makeUnauthorizedRequest({
    path,
    method = 'GET',
    headers = {},
    _queryParams = {},
    body,
    apiGateway = null,
}) {
    if (apiGateway && !config.aws.apiGateway[apiGateway]) {
        throw new Error(`makeUnauthorizedRequest(): Unknown API gateway "${apiGateway}"`);
    }

    const base = apiGateway ? config.aws.apiGateway[apiGateway] : config.aws.apiGateway.merchantBackend;
    const url = base + path;

    const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : body,
    });

    if (response.status !== 200) {
        let message = `${response.status} (${response.statusText})`;
        try {
            const payload = await response.json();
            const isServerlessError = payload.error && payload.currentRoute;
            const isHaftahaveError = payload.error && payload.message;
            if (isServerlessError) {
                message = payload.error;
            } else if (isHaftahaveError) {
                ({ message } = payload);
            } else {
                message = await response.text();
            }
        } catch (error) {
            message = await response.text();
        }

        throw new Error(message);
    }

    return response.json();
}

/**
 * @param {*} file
 * @returns {Promise<ManagedUpload.SendData>}
 */
export async function s3Upload(file) {
    await getRequestCredentials();

    const s3 = new AWS.S3({
        params: {
            Bucket: config.aws.s3.uploadBucket,
        },
    });
    const filename = `${AWS.config.credentials.identityId}-${Date.now()}-${file.name}`;

    return s3
        .upload({
            Key: filename,
            Body: file,
            ContentType: file.type,
            ACL: 'public-read',
        })
        .promise();
}
